/** IPS içerik indirme — tüm medya /ips-download endpoint'i üzerinden */

import { apiUrl } from './apiBase';

const TURKISH_MAP = {
  ğ: 'g',
  ü: 'u',
  ş: 's',
  ı: 'i',
  ö: 'o',
  ç: 'c',
  Ğ: 'g',
  Ü: 'u',
  Ş: 's',
  İ: 'i',
  Ö: 'o',
  Ç: 'c'
};

export function slugifyIpsLabel(text = '') {
  let value = String(text);
  Object.entries(TURKISH_MAP).forEach(([from, to]) => {
    value = value.split(from).join(to);
  });
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getFileExtension(fileName, url, mediaType) {
  const fromName = String(fileName || '').match(/(\.[a-z0-9]{2,5})$/i);
  if (fromName) return fromName[1].toLowerCase();

  const urlPath = String(url || '').split('?')[0];
  const fromUrl = urlPath.match(/(\.[a-z0-9]{2,5})$/i);
  if (fromUrl) return fromUrl[1].toLowerCase();

  if (mediaType === 'video' || String(mediaType || '').startsWith('video/')) return '.mp4';
  if (mediaType === 'image' || String(mediaType || '').startsWith('image/')) return '.jpg';
  return '.bin';
}

/** İndirme dosya adı (UI etiketi / ileride ZIP için). */
export function buildIpsDownloadFileName(content, file, fileIndex = 0, totalFiles = 1) {
  const date = content?.date || 'tarihsiz';
  const label = content?.planRowLabel || content?.title || content?.type || content?.id || 'icerik';
  const slug = slugifyIpsLabel(label) || 'icerik';
  const ext = getFileExtension(file?.fileName, file?.url, file?.mediaType);
  const indexSuffix = totalFiles > 1 ? `-${fileIndex + 1}` : '';
  const base = `${date}-${slug}${indexSuffix}`;
  return `${base}${ext}`.replace(/[^\w.\-]/g, '_');
}

/** İçerikteki indirilebilir dosyalar (tek veya çoklu), sıralı. */
export function getIpsContentFiles(content) {
  if (!content) return [];

  if (Array.isArray(content.mediaFiles) && content.mediaFiles.length) {
    return content.mediaFiles
      .map((entry, index) => ({
        order: entry?.order ?? index + 1,
        url: entry?.url || entry?.mediaUrl || '',
        fileName: entry?.fileName || entry?.name || '',
        mediaType: entry?.mediaType || entry?.type || 'image'
      }))
      .filter((entry) => entry.url)
      .sort((a, b) => a.order - b.order);
  }

  if (content.mediaUrl) {
    return [
      {
        order: 1,
        url: content.mediaUrl,
        fileName: content.fileName || '',
        mediaType: content.mediaType || ''
      }
    ];
  }

  return [];
}

export function isCarouselContent(content) {
  return getIpsContentFiles(content).length > 1;
}

export function isVideoMediaFile(file) {
  if (!file) return false;
  if (file.mediaType === 'video' || String(file.mediaType || '').startsWith('video/')) {
    return true;
  }
  const hint = `${file.url || ''} ${file.fileName || ''}`.toLowerCase();
  return /\.(mp4|mov|webm|m4v|quicktime)(\?|$)/.test(hint);
}

export function isImageMediaFile(file) {
  if (!file) return false;
  if (file.mediaType === 'image' || String(file.mediaType || '').startsWith('image/')) {
    return true;
  }
  const hint = `${file.url || ''} ${file.fileName || ''}`.toLowerCase();
  return /\.(jpe?g|png|webp)(\?|$)/.test(hint);
}

/** Cloud Function medya indirme endpoint'i. */
export function buildIpsMediaDownloadUrl(contentId, fileIndex = 0) {
  if (!contentId) return '';
  const params = new URLSearchParams({
    contentId: String(contentId),
    fileIndex: String(fileIndex)
  });
  return apiUrl(`/ips-download?${params.toString()}`);
}

/** @deprecated buildIpsMediaDownloadUrl kullanın */
export const buildIpsVideoDownloadUrl = buildIpsMediaDownloadUrl;

/** Önizleme için yeni sekme (indirme değil). */
export function openIpsMediaInNewTab(fileUrl) {
  if (!fileUrl) return false;
  try {
    const anchor = document.createElement('a');
    anchor.href = fileUrl;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    return true;
  } catch {
    const opened = window.open(fileUrl, '_blank', 'noopener,noreferrer');
    return !!opened;
  }
}

export function triggerIpsMediaDownload(contentId, fileIndex = 0) {
  const downloadUrl = buildIpsMediaDownloadUrl(contentId, fileIndex);
  if (!downloadUrl) return false;
  window.location.assign(downloadUrl);
  return true;
}

/** Tek dosya — endpoint üzerinden. */
export async function downloadIpsFile(contentId, fileIndex = 0) {
  if (!contentId) {
    return { success: false, method: 'error', error: 'missing-content-id' };
  }
  const success = triggerIpsMediaDownload(contentId, fileIndex);
  return success
    ? { success: true, method: 'endpoint' }
    : { success: false, method: 'error', error: 'missing-download-url' };
}

const DOWNLOAD_STAGGER_MS = 600;

/** Carousel / çoklu görsel — her dosya aynı endpoint. İleride ZIP buraya eklenebilir. */
export async function downloadAllIpsFiles(content, files) {
  const list = Array.isArray(files) ? files : getIpsContentFiles(content);
  if (!content?.id || !list.length) {
    return { success: false, successCount: 0, total: list.length || 0 };
  }

  let successCount = 0;
  for (let i = 0; i < list.length; i += 1) {
    if (triggerIpsMediaDownload(content.id, i)) {
      successCount += 1;
    }
    if (i < list.length - 1) {
      await new Promise((resolve) => window.setTimeout(resolve, DOWNLOAD_STAGGER_MS));
    }
  }

  return {
    success: successCount === list.length,
    successCount,
    total: list.length
  };
}
